// Dok 50–54 – Byggherrens organisation, delegering och lagefterlevnad.
// Bygger på AFS 2023:3 kap. 2 (byggherrens ansvar, kompetens, tillsyn och kontroll)
// samt AML 3 kap. 6 § om uppgiftsfördelning inom den egna organisationen.

export interface ByggherreSectionDef {
  key: string;
  title: string;
  hint: string;
  template: string;
}

export const BYGGHERRE_STATUS_LABELS: Record<string, string> = {
  utkast: "Utkast",
  granskning: "Under granskning",
  faststalld: "Fastställd",
  arkiverad: "Arkiverad",
};

/* ---------------- Dok 50 – Byggherrens hantering av byggarbetsmiljöarbete ---------------- */

export const BYGGHERRE_HANDLING_SECTIONS: ByggherreSectionDef[] = [
  {
    key: "bakgrund",
    title: "Bakgrund",
    hint: "Varför dokumentet finns och vilka krav det utgår från.",
    template:
      "Enligt AFS 2023:3 kap. 2 har byggherren det yttersta ansvaret för arbetsmiljöarbetet i bygg- och anläggningsprojekt. Det innebär att byggherren ska utöva nödvändig kontroll och tillsyn samt ha rutiner och metoder för att efterleva sitt ansvar. Organisation och resurser för arbetsmiljöarbetet ska finnas från projektets start till dess slut.\n\nByggherrens hantering av utsedda byggarbetsmiljösamordnare (Bas-P och Bas-U) regleras särskilt i AFS 2023:3 2 kap. 9–12, 15–19 samt 23–24 §§. Enligt 2 kap. 15 § ska byggherren ha egen kompetens som motsvarar projektets storlek, komplexitet och risknivå.",
  },
  {
    key: "syfte",
    title: "Syfte",
    hint: "Vad dokumentet ska åstadkomma.",
    template:
      "Syftet är att byggherrens representant löpande ska kunna dokumentera hur byggarbetsmiljöarbetet hanteras, organiseras, följs upp och kontrolleras – genom att utse och följa upp byggarbetsmiljösamordnare, anlita rätt projekterande resurser och tydliggöra vilka arbetsuppgifter som ingår i varje skede.",
  },
  {
    key: "omfattning",
    title: "Omfattning",
    hint: "Vilka verksamheter, roller och skeden som omfattas.",
    template:
      "Dokumentet omfattar rollen som byggherrens representant och byggarbetsmiljösamordnarnas (Bas-P och Bas-U) arbete i sin helhet, från projektets start till slut. Arbetsmiljöfrågorna är ingen egen fas utan en integrerad del av allt arbete i processen.\n\nByggnads- och anläggningsarbete omfattar bland annat schaktning, markarbeten, byggnadsarbete, montering och nedmontering av prefab, installation, ändringar, renovering, reparation, rivning, löpande och periodiskt underhåll, dränering och sanering. Undantag är bland annat drift och städning, sotning, filterbyten, gräsklippning, snöröjning och byte av ljuskällor.",
  },
  {
    key: "organisation",
    title: "Byggherrens organisation för byggarbetsmiljöarbete",
    hint: "Hur ansvaret är organiserat från ledning ner till representant.",
    template:
      "Arbetsuppgifter för byggherrens arbetsmiljöansvar är fördelade från styrelse/VD i led ned till en byggherrerepresentant, se Dok 51 Delegeringsordning och Dok 52 Delegeringsbrev.\n\nUnder planering och projektering består organisationen av byggherrens representant och Bas-P, tillsammans med eventuella andra arbetsmiljö- eller samordningsansvariga (till exempel vid fast driftställe eller flera entreprenader).\n\nUnder utförande består organisationen av byggherrens representant, Bas-P och Bas-U tillsammans med eventuella andra samordningsansvariga.",
  },
  {
    key: "kompetens",
    title: "Arbetsmiljökompetens inom byggherrens organisation",
    hint: "Vilken kompetens representanten har och hur den matchar projektets SKR-profil.",
    template:
      "Byggherren ska ha arbetsmiljökompetens som motsvarar projektets storlek, komplexitet och risknivå (SKR). Representanten ska själv ha den tid, kompetens och erfarenhet som krävs för att förstå och efterleva byggherrens ansvar samt utöva tillsyn och kontroll (AFS 2023:3 2 kap. 15 §).\n\nRepresentant: [namn, roll, kontaktuppgifter]\nUtbildning och erfarenhet: [BAS-P/BAS-U-utbildning, BAM, år i branschen, referensprojekt]\nBedömd tillräcklighet mot projektets SKR-profil: [motivering]\nEventuellt stöd: [intern arbetsmiljöspecialist eller anlitad resurs]",
  },
  {
    key: "roller",
    title: "Roller och ansvar inom byggarbetsmiljö",
    hint: "Vad respektive roll gör i projektet.",
    template:
      "Byggherrens representant: utser Bas-P och Bas-U skriftligt, kontrollerar deras kompetens, följer upp arbetet och beslutar om resurser.\nBas-P: samordnar arbetsmiljön under planering och projektering, upprättar arbetsmiljöplanen och överlämnar underlaget till Bas-U.\nBas-U: samordnar arbetsmiljön under utförandet, håller arbetsmiljöplanen aktuell och följer upp på plats.\nProjektör: projekterar bort risker och redovisar kvarvarande risker till Bas-P.\nEntreprenör: följer arbetsmiljöplanen, lämnar arbetsberedningar och rapporterar tillbud.",
  },
  {
    key: "verksamheter",
    title: "Byggherrens verksamheter",
    hint: "Projektverksamhet, förvaltning och underhåll – hur ansvaret ser ut i respektive del.",
    template:
      "Projektverksamhet: [beskriv hur byggarbetsmiljöansvaret hanteras i investerings- och nybyggnadsprojekt]\nAnläggning/fastighetsförvaltning: [beskriv hantering vid ombyggnad och åtgärder i förvaltad anläggning]\nUnderhåll och avhjälpande: [beskriv hantering vid löpande, periodiskt och avhjälpande underhåll, se Dok 54]",
  },
  {
    key: "utse",
    title: "Byggherren utser byggarbetsmiljösamordnare",
    hint: "Val mellan egen organisation och extern aktör, samt hur uppgifter fördelas.",
    template:
      "Byggherren kan vara Bas-P och Bas-U själv, utse en person i den egna organisationen eller utse en annan juridisk person. Utseendet ska alltid vara skriftligt, se Dok 05 Uppdragsbekräftelse.\n\nEgen organisation – fördel: närhet, kontroll och kunskap om verksamheten. Nackdel: kräver egen kompetens, arbetssätt och tid.\nExtern aktör – fördel: tillgång till specialistkompetens. Nackdel: kräver tydlig kravställning, uppföljning och kontroll av att uppgifterna faktiskt utförs.\n\nVid utseende av annan juridisk person ska det framgå vilka arbetsuppgifter som omfattas, vilka befogenheter och resurser som följer med samt vem som är handläggare.",
  },
  {
    key: "krav",
    title: "Krav på byggarbetsmiljösamordnare",
    hint: "Utbildning, kompetens, erfarenhet och hur den kontrolleras.",
    template:
      "Krav på Bas-P: utbildning i byggarbetsmiljösamordning, kunskap om projekteringsprocessen och erfarenhet av jämförbara projekt.\nKrav på Bas-U: utbildning i byggarbetsmiljösamordning, kunskap om produktion och erfarenhet av jämförbara arbetsplatser.\n\nKompetensen kontrolleras före utseendet genom intyg, referenser och genomgång av arbetssätt och rutiner. Kontrollen dokumenteras och följs upp löpande under uppdraget, se checklistan i Dok 53.",
  },
  {
    key: "projektorer",
    title: "Projektörer och samordnande projektör",
    hint: "Krav på projekterande resurser.",
    template:
      "Byggherren ska anlita projektörer som har kunskap om arbetsmiljökraven och som redovisar kvarvarande risker för både byggskede och bruksskede (Dok 16, Dok 18, Dok 19). Vid flera projektörer ska en samordnande projektör utses och gränssnitten mellan disciplinerna hanteras.",
  },
  {
    key: "byten",
    title: "Byte och överlämningar",
    hint: "Vad som gäller när en roll byts under projektet.",
    template:
      "Vid byte av byggherrerepresentant, Bas-P eller Bas-U ska en dokumenterad överlämning ske: pågående frågor, öppna åtgärder, gällande arbetsmiljöplan och kvarstående risker. Nytt skriftligt utseende upprättas och delegeringsbrevet uppdateras.",
  },
  {
    key: "handelser",
    title: "Händelser och avvikelser",
    hint: "Hur byggherren agerar vid tillbud, olyckor och brister.",
    template:
      "Vid allvarliga tillbud och olyckor ska byggherren informeras, händelsen anmälas enligt AML 3 kap. 3 a § och arbetsmiljöplanen revideras vid behov (Dok 28). Brister i samordnarnas arbete hanteras skriftligt och följs upp i checklistan (Dok 53).",
  },
  {
    key: "stallforetradare",
    title: "Ställföreträdare för arbetsmiljöarbetet",
    hint: "Backup när representanten inte kan utföra sina uppgifter.",
    template:
      "En ställföreträdare ska vara utsedd för att säkra att byggherrens arbetsuppgifter kan utföras vid frånvaro, sjukdom eller vakans.\n\nStällföreträdare: [namn, roll, kontaktuppgifter]\nGäller vid: [frånvaro över X dagar, vakans, jäv]\nStällföreträdaren har samma befogenheter som representanten under perioden.",
  },
];

/* ---------------- Dok 51 – Delegeringsordning ---------------- */

export const DELEGATION_ORDER_SECTIONS: ByggherreSectionDef[] = [
  {
    key: "bakgrund",
    title: "Bakgrund",
    hint: "Varför delegeringsordningen behövs.",
    template:
      "Arbetsmiljölagstiftningen ställer krav på att byggherren har den organisation som krävs för byggarbetsmiljöarbetet, samt att organisationen har nödvändig backup om en roll saknas eller inte kan utföra sina arbetsuppgifter.",
  },
  {
    key: "syfte",
    title: "Syfte",
    hint: "Vad PM:et ska visa.",
    template:
      "Detta PM redovisar vilken delegeringsordning och organisation som gäller för [företag/verksamhet], så att det går att överblicka att byggherren är representerad och utför nödvändiga arbetsuppgifter i den byggnads- och anläggningsverksamhet som bedrivs.",
  },
  {
    key: "omfattning",
    title: "Omfattning",
    hint: "Från vem till vem delegeringen sträcker sig.",
    template:
      "Dokumentet omfattar organisation och fördelning av arbetsuppgifter för byggherrens arbetsmiljöansvar, från [VD/styrelse] ned till den verksamhet som bedriver byggnads- och anläggningsarbeten.",
  },
  {
    key: "ordning",
    title: "Organisation och delegeringsordning",
    hint: "Kedjan led för led. Specificera vem som får delegera vidare.",
    template:
      "1. [VD/styrelseordförande/styrelse] – med rätt att fördela rollen som byggherrerepresentant vidare till\n2. [chef affärsområde] – med rätt att fördela vidare till\n3. [verksamhetschef] – med rätt att fördela vidare till\n4. [projektchef/underhållschef/fastighetschef] – med rätt att utse byggarbetsmiljösamordnare och låta annan (t.ex. projektledare) handlägga de arbetsuppgifter som åligger byggherrerepresentanten.\n\nByggherrerepresentanter enligt punkt 4 får inte fördela rollen vidare, men får låta annan handlägga arbetsuppgifterna. Handläggning fråntar inte representanten ansvaret för kontroll och uppföljning.",
  },
  {
    key: "fordelning",
    title: "Fördelning av arbetsuppgifter",
    hint: "Text att komplettera befintlig delegeringsmall med.",
    template:
      "I egenskap av [VD/överordnad chef] fördelar jag till dig de uppgifter och befogenheter som krävs för att fullgöra vårt arbetsmiljöansvar när vi agerar som byggherre vid byggnads- och anläggningsarbeten. Du är därmed representant för byggherren i din roll som [chef över projekt-/underhållsverksamhet där byggnads- och anläggningsarbeten förekommer]. Utförande av arbetsuppgifter sker enligt Dok 50 och checklistan i Dok 53.",
  },
  {
    key: "befogenheter",
    title: "Befogenheter och resurser",
    hint: "Vad mottagaren får besluta om och vilka resurser som följer med.",
    template:
      "Med fördelningen följer befogenhet att besluta om åtgärder, upphandla resurser inom [beloppsgräns], utse byggarbetsmiljösamordnare, stoppa arbeten vid omedelbar fara samt begära in kompletterande underlag från projektörer och entreprenörer.",
  },
  {
    key: "returnering",
    title: "Returnering av arbetsuppgift",
    hint: "Vad mottagaren gör när förutsättningar saknas.",
    template:
      "Om kompetens, tid, befogenheter eller resurser inte räcker för att utföra en arbetsuppgift ska uppgiften skriftligen returneras till den som fördelat den. Returneringen dokumenteras och följs upp tills frågan är löst.",
  },
  {
    key: "backup",
    title: "Ställföreträdare och backup",
    hint: "Hur organisationen säkras vid vakans eller frånvaro.",
    template:
      "För varje led ska en ställföreträdare finnas utsedd. Vid vakans övergår arbetsuppgifterna tillfälligt till närmast överordnad nivå tills en ny mottagare är utsedd och delegeringsbrevet undertecknat.",
  },
  {
    key: "uppfoljning",
    title: "Uppföljning och revidering",
    hint: "Hur ofta ordningen ses över.",
    template:
      "Delegeringsordningen ses över minst en gång per år samt vid organisationsförändring, byte av person i något led eller ändrad lagstiftning. Revideringen dokumenteras med datum och ansvarig.",
  },
];

/* ---------------- Dok 52 – Delegeringsbrev ---------------- */

export const DELEGATION_LETTER_STATUS_LABELS: Record<string, string> = {
  utkast: "Utkast",
  skickat: "Skickat för underskrift",
  undertecknat: "Undertecknat",
  aterlamnat: "Återlämnat",
  upphort: "Upphört",
};

export const DELEGATION_LETTER_TEMPLATE = {
  background:
    "Vår verksamhet regleras av ett stort antal bestämmelser inom arbetsmiljöområdet, i detta fall vårt ansvar som byggherre. Vid byggnads- och anläggningsarbeten ansvarar vi för att arbetsmiljöfrågor beaktas i varje skede – från idé till överlämning för bruksskede och framtida rivning. Omfattningen är sådan att företagsledningen inte i detalj kan leda och övervaka det arbete som krävs av en byggherre, varför arbetsuppgifter och befogenheter behöver fördelas inom organisationen.",
  scope:
    "Följande ska iakttas i tillämpliga delar: arbetsmiljölagen (AML) 3 kap. 6 §, Arbetsmiljöverkets föreskrifter om projektering och byggarbetsmiljösamordning samt utformning av arbetsplatser (AFS 2023:3 och AFS 2023:11), och övriga tillämpliga föreskrifter där byggherrens krav på tillsyn och kontroll förekommer.",
  competence:
    "Du ansvarar för att bedöma och skaffa den utbildning som rollen kräver samt för att bevaka ändringar i regelverket. Genom denna uppgiftsfördelning företräder du bolaget i frågor som rör byggherrens arbetsmiljöansvar.",
  authority:
    "Fördelningen ger dig rätt att utse byggarbetsmiljösamordnare (Bas-P och Bas-U), kontrollera deras kompetens, följa upp deras arbete, besluta om nödvändiga arbetsmiljöåtgärder och avbryta arbeten vid omedelbar fara.",
  returnClause:
    "Om du saknar kompetens, tid, befogenheter eller resurser för att utföra en arbetsuppgift ska du skriftligen returnera uppgiften till mig. Fördelningen gäller tills den skriftligen återkallas eller din roll upphör.",
};

/* ---------------- Dok 53 – Checklista lagefterlevnad byggherre ---------------- */

export const BYGGHERRE_CHECKLIST_PHASES: { key: string; label: string }[] = [
  { key: "planering", label: "Lagefterlevnad under planering" },
  { key: "projektering", label: "Lagefterlevnad under projektering" },
  { key: "utforande", label: "Lagefterlevnad under utförande" },
  { key: "slutdokumentation", label: "Lagefterlevnad under slutdokumentation" },
];

export const BYGGHERRE_CHECKLIST_STATUS_LABELS: Record<string, string> = {
  planerad: "Planerad",
  pagar: "Pågår",
  ok: "OK",
  ej_ok: "EJ OK",
  ej_aktuell: "N/A",
};

export interface ByggherreChecklistTemplateRow {
  phase: string;
  activity: string;
  requirement: string;
  question: string;
  method: string;
  /** Obligatorisk punkt – skedet kan inte avslutas förrän den är OK eller N/A med motivering. */
  required?: boolean;
}

/** Kopplar projektets skeden (Dok 03) till checklistans skeden i Dok 53. */
export const PROJECT_PHASE_TO_CHECKLIST_PHASE: Record<string, string> = {
  program: "planering",
  system: "projektering",
  detalj: "projektering",
  upphandling: "projektering",
  bygg: "utforande",
  forvaltning: "slutdokumentation",
};

export const BYGGHERRE_CHECKLIST_TEMPLATE: ByggherreChecklistTemplateRow[] = [
  // Planering
  {
    phase: "planering",
    activity: "Organisation och delegering",
    requirement: "AML 3 kap. 6 § · AFS 2023:3 2 kap. 15 §",
    question: "Finns en obruten delegeringskedja och ett undertecknat delegeringsbrev för representanten?",
    method: "Vid projektstart · Dok 51 och Dok 52",
    required: true,
  },
  {
    phase: "planering",
    activity: "Egen arbetsmiljökompetens",
    requirement: "AFS 2023:3 2 kap. 15 §",
    question: "Har byggherrens representant kompetens och tid som motsvarar projektets storlek, komplexitet och risknivå?",
    method: "Vid projektstart och vid ändrad omfattning · Dok 50 och SKR-profil (Dok 03)",
  },
  {
    phase: "planering",
    activity: "Utse Bas-P",
    requirement: "AFS 2023:3 2 kap. 9–12 §§",
    question: "Är Bas-P utsedd skriftligt och har uppdragets omfattning bekräftats?",
    method: "Före projekteringsstart · Dok 05",
    required: true,
  },
  {
    phase: "planering",
    activity: "Kontroll av kompetens hos Bas-P",
    requirement: "AFS 2023:3 2 kap. 16–17 §§",
    question: "Är utbildning, erfarenhet och arbetssätt hos Bas-P kontrollerade och dokumenterade?",
    method: "Före utseendet · intyg och referenser",
    required: true,
  },
  {
    phase: "planering",
    activity: "Ställföreträdare",
    requirement: "AML 3 kap. 6 §",
    question: "Finns en utsedd ställföreträdare om representanten inte kan utföra sina uppgifter?",
    method: "Vid projektstart · Dok 51",
  },
  {
    phase: "planering",
    activity: "Förhandsanmälan",
    requirement: "AFS 2023:3 2 kap. 23–24 §§",
    question: "Är förhandsanmälan gjord och anslagen när kriterierna är uppfyllda?",
    method: "Före byggstart · Dok 04",
    required: true,
  },
  // Projektering
  {
    phase: "projektering",
    activity: "Krav på projektörer",
    requirement: "AFS 2023:3 3 kap.",
    question: "Är arbetsmiljökrav ställda i projektörernas uppdrag och redovisas kvarvarande risker?",
    method: "Vid upphandling och leverans · Dok 17, Dok 16, Dok 19",
    required: true,
  },
  {
    phase: "projektering",
    activity: "Utredning av arbetsmiljöpåverkan",
    requirement: "AFS 2023:3 2 kap.",
    question: "Har utredning gjorts av hur projektet påverkar arbetsmiljön i byggskedet och i bruksskedet?",
    method: "Under projektering · Dok 10, Dok 11, Dok 02",
  },
  {
    phase: "projektering",
    activity: "Arbetsmiljöplan upprättad",
    requirement: "AFS 2023:3 2 kap. 18–19 §§",
    question: "Finns en arbetsmiljöplan innan arbetsplatsen etableras och är den anpassad till projektet?",
    method: "Före etablering · Dok 12",
    required: true,
  },
  {
    phase: "projektering",
    activity: "Uppföljning av Bas-P",
    requirement: "AFS 2023:3 2 kap. 15–17 §§",
    question: "Har byggherren följt upp att Bas-P faktiskt utför sina arbetsuppgifter?",
    method: "Löpande, minst per skede · granskningsöversikt",
  },
  {
    phase: "projektering",
    activity: "Tidplan och resurser",
    requirement: "AFS 2023:3 2 kap.",
    question: "Är tidplanen granskad så att arbetena kan utföras säkert och med rimliga resurser?",
    method: "Före utförandeskedet · Dok 06",
  },
  // Utförande
  {
    phase: "utforande",
    activity: "Utse Bas-U",
    requirement: "AFS 2023:3 2 kap. 9–12 §§",
    question: "Är Bas-U utsedd skriftligt före utförandeskedet och har överlämningen från Bas-P skett?",
    method: "Före byggstart · Dok 05 och Dok 20",
    required: true,
  },
  {
    phase: "utforande",
    activity: "Kontroll av kompetens hos Bas-U",
    requirement: "AFS 2023:3 2 kap. 16–17 §§",
    question: "Är utbildning, erfarenhet och arbetssätt hos Bas-U kontrollerade och dokumenterade?",
    method: "Före utseendet · intyg och referenser",
    required: true,
  },
  {
    phase: "utforande",
    activity: "Tillsyn och kontroll på plats",
    requirement: "AFS 2023:3 2 kap. 15 §",
    question: "Utövar byggherren egen tillsyn genom platsbesök, skyddsronder eller uppföljningsmöten?",
    method: "Löpande enligt överenskommen frekvens · Dok 43",
  },
  {
    phase: "utforande",
    activity: "Arbetsmiljöplanen hålls aktuell",
    requirement: "AFS 2023:3 2 kap. 19 §",
    question: "Uppdateras arbetsmiljöplanen när arbetsmetoder, etapper eller risker ändras?",
    method: "Löpande · Dok 12 med versionshistorik",
    required: true,
  },
  {
    phase: "utforande",
    activity: "Tillbud och olyckor",
    requirement: "AML 3 kap. 3 a §",
    question: "Rapporteras allvarliga händelser till byggherren och anmäls de till Arbetsmiljöverket?",
    method: "Vid varje händelse · Dok 28",
  },
  {
    phase: "utforande",
    activity: "Byten och överlämningar",
    requirement: "AFS 2023:3 2 kap. 9–12 §§",
    question: "Dokumenteras byte av samordnare med nytt utseende och överlämning av öppna frågor?",
    method: "Vid varje byte · Dok 05 och Dok 50",
  },
  // Slutdokumentation
  {
    phase: "slutdokumentation",
    activity: "Arbetsmiljödokumentation överlämnad",
    requirement: "AFS 2023:3 2 kap.",
    question: "Är arbetsmiljödokumentationen med kvarvarande risker överlämnad till förvaltningen?",
    method: "Vid slutskede · Dok 22 och Dok 31",
    required: true,
  },
  {
    phase: "slutdokumentation",
    activity: "Funktionskrav bruksskede verifierade",
    requirement: "AFS 2023:11",
    question: "Är funktionskraven för bruksskedet verifierade i det färdiga byggnadsverket?",
    method: "Före överlämning · Dok 02 och Dok 33",
    required: true,
  },
  {
    phase: "slutdokumentation",
    activity: "Arkivering",
    requirement: "AFS 2023:3 2 kap.",
    question: "Är arbetsmiljöplan, ronder, tillbud och beslut arkiverade och spårbara?",
    method: "Vid avslut · exportlogg med verifieringskod",
    required: true,
  },
];

/* ---------------- Dok 54 – Fördelning av arbetsmiljöuppgifter (A/HL) ---------------- */

export const DUTY_ROLE_CODES: Record<string, string> = {
  A: "A – Ansvarig",
  HL: "HL – Handlägger",
  I: "I – Informeras",
  "-": "– Ingen uppgift",
};

export interface DutyTemplateRow {
  task: string;
  representative: string;
  handler: string;
  basp: string;
  basu: string;
}

export const DUTY_ASSIGNMENT_TEMPLATE: DutyTemplateRow[] = [
  { task: "Besluta om projektet ska genomföras och avsätta resurser", representative: "A", handler: "-", basp: "-", basu: "-" },
  { task: "Utse Bas-P och Bas-U skriftligt", representative: "A", handler: "HL", basp: "-", basu: "-" },
  { task: "Kontrollera att Bas-P och Bas-U har erforderlig kompetens", representative: "A", handler: "HL", basp: "-", basu: "-" },
  { task: "Ställa arbetsmiljökrav i upphandling av projektörer och entreprenörer", representative: "A", handler: "HL", basp: "I", basu: "I" },
  { task: "Lämna förhandsanmälan till Arbetsmiljöverket", representative: "A", handler: "HL", basp: "I", basu: "I" },
  { task: "Upprätta arbetsmiljöplan innan arbetsplatsen etableras", representative: "I", handler: "-", basp: "A", basu: "I" },
  { task: "Samordna projektörernas kvarvarande risker", representative: "I", handler: "-", basp: "A", basu: "I" },
  { task: "Hålla arbetsmiljöplanen aktuell under utförandet", representative: "I", handler: "-", basp: "I", basu: "A" },
  { task: "Samordna entreprenörer och gemensamma skyddsanordningar", representative: "-", handler: "-", basp: "-", basu: "A" },
  { task: "Genomföra skyddsronder och följa upp åtgärder", representative: "I", handler: "-", basp: "-", basu: "A" },
  { task: "Utöva tillsyn och kontroll över samordnarnas arbete", representative: "A", handler: "HL", basp: "I", basu: "I" },
  { task: "Hantera tillbud och olyckor samt anmäla allvarliga händelser", representative: "A", handler: "HL", basp: "I", basu: "HL" },
  { task: "Ta emot och arkivera arbetsmiljödokumentationen inför förvaltning", representative: "A", handler: "HL", basp: "I", basu: "HL" },
];
